/**
 * Parse and validate a sales-agent CSV.
 *
 * The file comes from a spreadsheet a person edited, so every assumption a
 * parser can make is one this will meet head on: quoted fields with commas
 * inside, a BOM Excel adds silently, Thai names saved as TIS-620 because that is
 * still what "CSV" means in Excel on Windows Thai, thousands separators, a ฿
 * sign, and a trailing blank line.
 *
 * Nothing here writes. It turns bytes into rows plus a list of what is wrong
 * with them, so the caller can show both before anything reaches the database.
 *
 * @module services/brokerImport/parseBrokerCsv
 */

import { z } from 'zod';

/** Translator, passed in — this module has no React context to hook from. */
export type TFn = (key: string, options?: Record<string, unknown>) => string;

/** Columns the file must supply. ROI and commission are derived, never read. */
export const REQUIRED_COLUMNS = [
  'agent_name',
  'agent_type',
  'spend',
  'conversions',
] as const;

export const OPTIONAL_COLUMNS = ['revenue', 'commission_rate'] as const;

export interface ParsedBrokerRow {
  /** 1-based line in the file as the user sees it, header included. */
  line: number;
  agentName: string;
  agentType: string;
  spend: number;
  conversions: number;
  revenue: number | null;
  commissionRate: number | null;
}

export interface RowProblem {
  line: number;
  column: string;
  message: string;
}

export interface ParseResult {
  rows: ParsedBrokerRow[];
  problems: RowProblem[];
  /** Set when the whole file is unusable — nothing was parsed. */
  fatal: string | null;
}

/**
 * A number as a spreadsheet writes it: "1,234.56", "฿1,234", " 12 ", "".
 * Rejects anything left over rather than letting parseFloat stop at the first
 * bad character and return a plausible wrong number — "12abc" must not be 12.
 */
export const parseSpreadsheetNumber = (raw: string): number | null => {
  const cleaned = raw.replace(/[฿,\s]/g, '').replace(/^\((.*)\)$/, '-$1');
  if (cleaned === '') return null;
  if (!/^-?\d*\.?\d+$/.test(cleaned)) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

/**
 * Split one CSV line, honouring quotes. A doubled quote inside a quoted field is
 * a literal quote — the same convention this app's own export writes.
 */
export const splitCsvLine = (line: string): string[] => {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; } else { inQuotes = false; }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      out.push(cur); cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((c) => c.trim());
};

/**
 * U+FFFD is what TextDecoder emits for bytes that are not valid UTF-8. Excel on
 * a Thai Windows install saves CSV as TIS-620, so every Thai agent name arrives
 * as replacement characters — readable enough to import and wrong forever after.
 * Better to refuse the file and say which setting to change.
 */
export const looksMisencoded = (text: string): boolean => text.includes('�');

/**
 * Built per parse rather than once at module load, because the validation
 * messages are translated and a module-level schema would freeze whichever
 * language happened to be active when the file was first imported.
 */
const buildRowSchema = (t: TFn) => z.object({
  agentName: z.string().min(1, t('brokerImport.needAgentName')),
  agentType: z.string().min(1, t('brokerImport.needType')),
  spend: z.number().nonnegative(t('brokerImport.notNegative')),
  conversions: z.number().int(t('brokerImport.wholeNumber')).nonnegative(t('brokerImport.notNegative')),
  revenue: z.number().nonnegative(t('brokerImport.notNegative')).nullable(),
  commissionRate: z
    .number()
    .min(0, t('brokerImport.between0and100'))
    .max(100, t('brokerImport.between0and100'))
    .nullable(),
});

export const parseBrokerCsv = (text: string, t: TFn): ParseResult => {
  const problems: RowProblem[] = [];
  const rowSchema = buildRowSchema(t);

  if (looksMisencoded(text)) {
    return {
      rows: [],
      problems: [],
      fatal:
        t('brokerImport.notUtf8'),
    };
  }

  // Excel prepends a BOM; left in place it becomes part of the first header name.
  const lines = text.replace(/^\uFEFF/, '').split(/\r\n|\n|\r/);
  const headerIndex = lines.findIndex((l) => l.trim() !== '');
  if (headerIndex === -1) return { rows: [], problems: [], fatal: t('brokerImport.emptyFile') };

  const header = splitCsvLine(lines[headerIndex]).map((h) => h.toLowerCase());
  const missing = REQUIRED_COLUMNS.filter((c) => !header.includes(c));
  if (missing.length > 0) {
    return {
      rows: [],
      problems: [],
      fatal: t('brokerImport.missingColumns', { missing: missing.join(', '), required: REQUIRED_COLUMNS.join(', ') }),
    };
  }

  const at = (cells: string[], col: string): string => {
    const i = header.indexOf(col);
    return i === -1 ? '' : (cells[i] ?? '');
  };

  const rows: ParsedBrokerRow[] = [];
  const seen = new Map<string, number>();

  for (let i = headerIndex + 1; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim() === '') continue; // trailing newline, or a spacer row
    const lineNo = i + 1;
    const cells = splitCsvLine(line);

    const numeric = (col: string): { value: number | null; bad: boolean } => {
      const raw = at(cells, col);
      if (raw === '') return { value: null, bad: false };
      const n = parseSpreadsheetNumber(raw);
      if (n === null) {
        problems.push({ line: lineNo, column: col, message: t('brokerImport.notANumber', { value: raw }) });
        return { value: null, bad: true };
      }
      return { value: n, bad: false };
    };

    const spend = numeric('spend');
    const conversions = numeric('conversions');
    const revenue = numeric('revenue');
    const commissionRate = numeric('commission_rate');
    if (spend.bad || conversions.bad || revenue.bad || commissionRate.bad) continue;

    const candidate = {
      agentName: at(cells, 'agent_name'),
      agentType: at(cells, 'agent_type'),
      spend: spend.value ?? 0,
      conversions: conversions.value ?? 0,
      revenue: revenue.value,
      commissionRate: commissionRate.value,
    };

    const checked = rowSchema.safeParse(candidate);
    if (!checked.success) {
      for (const issue of checked.error.issues) {
        problems.push({
          line: lineNo,
          column: String(issue.path[0] ?? '—'),
          message: issue.message,
        });
      }
      continue;
    }

    // Two rows for one agent in a single file cannot both be right, and the
    // database would reject the second anyway — better to say which lines clash
    // than to surface a constraint error later.
    const key = checked.data.agentName.toLowerCase();
    const previous = seen.get(key);
    if (previous !== undefined) {
      problems.push({
        line: lineNo,
        column: 'agent_name',
        message: t('brokerImport.duplicateRow', { line: previous }),
      });
      continue;
    }
    seen.set(key, lineNo);

    rows.push({ line: lineNo, ...checked.data });
  }

  return { rows, problems, fatal: null };
};

/** ROI and commission are computed here, never accepted from the file. */
export const deriveBrokerFigures = (row: ParsedBrokerRow) => ({
  avgRoi: row.revenue !== null && row.spend > 0 ? row.revenue / row.spend : null,
  totalCommission:
    row.revenue !== null && row.commissionRate !== null
      ? (row.revenue * row.commissionRate) / 100
      : null,
});
